import {
  AlertCircle,
  BarChart3,
  BookUser,
  CreditCard,
  DollarSign,
  FileText,
  Home,
  LayoutDashboard,
  ListChecks,
  Mail,
  Receipt,
  Settings,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface RouteConfig {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

// Map of parent paths that don't have their own route to their default child page
const parentRedirects: Record<string, string> = {
  '/collections': '/collections/dashboard',
  '/payments': '/payments/receipts',
};

const routeConfig: Record<string, RouteConfig> = {
  dashboard: {
    label: 'Dashboard',
    icon: Home,
  },
  customers: {
    label: 'Customers',
    icon: Users,
  },
  contacts: {
    label: 'Contacts',
    icon: BookUser,
  },
  transactions: {
    label: 'Transactions',
    icon: CreditCard,
  },
  payments: {
    label: 'Payments',
    icon: DollarSign,
  },
  collections: {
    label: 'Collections',
    icon: ListChecks,
  },
  queue: {
    label: 'Collections Queue',
    icon: ListChecks,
  },
  case: {
    label: 'Case',
    icon: FileText,
  },
  promises: {
    label: 'Promise to Pay',
    icon: ListChecks,
  },
  strategies: {
    label: 'Strategies',
    icon: Target,
  },
  disputes: {
    label: 'Disputes',
    icon: AlertCircle,
  },
  'credit-scoring': {
    label: 'Credit Scoring',
    icon: BarChart3,
  },
  settings: {
    label: 'Settings',
    icon: Settings,
  },
  users: {
    label: 'Users',
    icon: Users,
  },
  roles: {
    label: 'Roles & Permissions',
    icon: Settings,
  },
  templates: {
    label: 'Templates',
    icon: FileText,
  },
  'create-pdf': {
    label: 'Create Statement Template',
    icon: FileText,
  },
  edit: {
    label: 'Edit Template',
    icon: FileText,
  },
  receipts: {
    label: 'Receipts',
    icon: Receipt,
  },
  allocate: {
    label: 'Allocate Receipt',
    icon: DollarSign,
  },
};

function resolveDynamicSegment(
  segment: string,
  parentSegment: string | undefined,
  currentPath: string,
): BreadcrumbItem | null {
  if (parentSegment === 'customers') {
    return { label: 'Detail', href: currentPath };
  }

  if (parentSegment === 'roles') {
    const roleName = segment
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return { label: roleName, href: currentPath };
  }

  const isPromiseId = parentSegment === 'promises' || /^PTP-\d+$/i.test(segment);
  const isCaseId = parentSegment === 'case' || /^[A-Z]+-\d+$/i.test(segment);
  if (isPromiseId || isCaseId) {
    return { label: 'Detail', href: currentPath };
  }

  if (/^[0-9a-f-]+$/i.test(segment)) {
    return { label: 'Detail', href: currentPath };
  }

  return null;
}

export function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Always start with Dashboard
  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'Dashboard',
      href: '/',
      icon: Home,
    },
  ];

  // Handle special route: /analytics-reporting/multi-company/company/:companyId -> Multi-Company Dashboard > Company
  if (
    pathSegments.length >= 4 &&
    pathSegments[0] === 'analytics-reporting' &&
    pathSegments[1] === 'multi-company' &&
    pathSegments[2] === 'company'
  ) {
    breadcrumbs.push(
      { label: 'Multi-Company Dashboard', href: '/analytics-reporting/multi-company', icon: LayoutDashboard },
      { label: 'Company', href: `/analytics-reporting/multi-company/company/${pathSegments[3]}` },
    );
    return breadcrumbs;
  }

  // Handle analytics-reporting routes -> Dashboard > <Page>
  // Skip the intermediate 'Analytics & Reporting' breadcrumb (no standalone page exists for it)
  if (pathSegments.length === 2 && pathSegments[0] === 'analytics-reporting') {
    const analyticsPages: Record<string, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
      'ageing-analysis': { label: 'Ageing Analysis', icon: TrendingUp },
      'multi-company': { label: 'Multi-Company Dashboard', icon: LayoutDashboard },
      emailing: { label: 'Emailing', icon: Mail },
    };
    const page = analyticsPages[pathSegments[1]];
    if (page) {
      breadcrumbs.push({ label: page.label, href: `/analytics-reporting/${pathSegments[1]}`, icon: page.icon });
      return breadcrumbs;
    }
  }

  // Handle special route: /settings/dispute-types/:id/workflow -> Dispute Types > Edit Dispute Type
  if (
    pathSegments.length >= 4 &&
    pathSegments[0] === 'settings' &&
    pathSegments[1] === 'dispute-types' &&
    pathSegments[3] === 'workflow'
  ) {
    breadcrumbs.push(
      { label: 'Dispute Types', href: '/settings/dispute-types' },
      { label: 'Edit Dispute Type', href: `/settings/dispute-types/${pathSegments[2]}/workflow` },
    );
    return breadcrumbs;
  }

  // Handle special route: /payments/allocate/:receiptId -> Receipts > Allocate Receipt
  if (pathSegments.length >= 3 && pathSegments[0] === 'payments' && pathSegments[1] === 'allocate') {
    breadcrumbs.push(
      { label: 'Receipts', href: '/payments/receipts', icon: Receipt },
      { label: 'Allocate Receipt', href: `/payments/allocate/${pathSegments[2]}` },
    );
    return breadcrumbs;
  }

  // Handle special route: /settings/templates/builder/:id -> Templates > Statement Builder
  if (
    pathSegments.length >= 4 &&
    pathSegments[0] === 'settings' &&
    pathSegments[1] === 'templates' &&
    pathSegments[2] === 'builder'
  ) {
    breadcrumbs.push(
      { label: 'Templates', href: '/settings/templates', icon: FileText },
      { label: 'Statement Builder', href: `/settings/templates/builder/${pathSegments[3]}` },
    );
    return breadcrumbs;
  }

  // Handle special route: /settings/templates/create-pdf -> Templates > Create Statement
  if (
    pathSegments.length >= 3 &&
    pathSegments[0] === 'settings' &&
    pathSegments[1] === 'templates' &&
    pathSegments[2] === 'create-pdf'
  ) {
    breadcrumbs.push(
      { label: 'Templates', href: '/settings/templates', icon: FileText },
      { label: 'Create Statement Template', href: '/settings/templates/create-pdf', icon: FileText },
    );
    return breadcrumbs;
  }

  // Handle special route: /payments/receipts -> Dashboard > All
  // Payments now has only one child (Receipts), so the intermediate 'Payments' breadcrumb is
  // unnecessary and caused duplicate React keys when href collision made reconciliation fail.
  if (pathSegments.length === 2 && pathSegments[0] === 'payments' && pathSegments[1] === 'receipts') {
    breadcrumbs.push({ label: 'All', href: '/payments/receipts', icon: Receipt });
    return breadcrumbs;
  }

  // Segments that represent list/main pages — show "All" when they are the last breadcrumb
  const listPageSegments = new Set([
    'customers',
    'transactions',
    'promises',
    'disputes',
    'queue',
    'strategies',
    'templates',
    'receipts',
    'users',
    'credit-scoring',
    'roles',
  ]);

  // Build breadcrumbs from path segments
  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;

    // Skip 'settings' parent segment — it has no index page
    if (segment === 'settings' && index === 0) {
      return;
    }

    const config = routeConfig[segment];

    // Skip 'roles' segment - it will be handled by modifying the 'users' breadcrumb
    if (segment === 'roles' && pathSegments[index - 1] === 'users') {
      return;
    }

    // Skip 'case' segment when followed by a case ID — show case ID directly
    if (segment === 'case' && index < pathSegments.length - 1) {
      return;
    }

    if (!config) {
      const dynamicItem = resolveDynamicSegment(segment, pathSegments[index - 1], currentPath);
      if (dynamicItem) {
        breadcrumbs.push(dynamicItem);
      } else {
        breadcrumbs.push({
          label: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
          href: currentPath,
        });
      }
    } else if (segment === 'users' && pathSegments[index + 1] === 'roles') {
      const isEffectivelyLast = index + 1 === pathSegments.length - 1;
      breadcrumbs.push({
        label: isEffectivelyLast ? 'All' : 'Roles & Permissions',
        href: `${currentPath}?tab=roles`,
        icon: Settings,
      });
    } else {
      const isLastSegment = index === pathSegments.length - 1;
      breadcrumbs.push({
        label: isLastSegment && listPageSegments.has(segment) ? 'All' : config.label,
        href: parentRedirects[currentPath] || currentPath,
        icon: config.icon,
      });
    }
  });

  return breadcrumbs;
}
